<template>
    <div class="p-4 flex flex-col gap-4 font-inter pb-20">
        <div class="flex items-center gap-4">
            <h1 class="text-2xl font-bold">Cardápio</h1>
            <div class="flex-1">
                <input 
                    v-model="searchQuery" 
                    type="text" 
                    placeholder="Pesquisar produto..." 
                    class="w-full p-3 bg-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 transition"
                />
            </div>
        </div>

        <!-- Categories horizontal scroll -->
        <div class="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            <button 
                @click="selectCategory(null)"
                :class="!selectedCategoryId ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600'"
                class="px-4 py-2 rounded-full whitespace-nowrap font-medium transition duration-200"
            >
                Todos
            </button>
            <button 
                v-for="category in categories" 
                :key="category.id"
                @click="selectCategory(category.id)"
                :class="selectedCategoryId === category.id ? 'bg-primary text-white' : 'bg-gray-100 text-gray-600'"
                class="px-4 py-2 rounded-full whitespace-nowrap font-medium transition duration-200"
            >
                {{ category.name }}
            </button>
        </div>

        <div class="flex justify-between items-center mt-2">
            <h2 class="text-lg font-semibold text-gray-700">{{ currentCategoryName }}</h2>
            <span class="text-sm text-gray-400">{{ filteredProducts.length }} produtos</span>
        </div>

        <!-- Products Grid -->
        <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" v-if="filteredProducts.length">
            <div 
                v-for="product in filteredProducts" 
                :key="product.id"
                @click="setModalProduct(product)"
                class="bg-white rounded-2xl overflow-hidden box-shadow border hover:border-primary transition duration-300 cursor-pointer flex flex-col"
            >
                <div class="aspect-square relative overflow-hidden bg-gray-50">
                    <img 
                        v-if="product.media?.length" 
                        :src="apiBaseUrl + '/storage/' + product.media[0].path" 
                        class="w-full h-full object-cover transition duration-500 hover:scale-110"
                    />
                    <div v-else class="w-full h-full flex items-center justify-center text-gray-300">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>
                    </div>
                    <div class="absolute bottom-2 right-2 bg-white/90 backdrop-blur px-2 py-1 rounded-lg text-xs font-bold text-gray-800">
                        {{ product.price.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }}
                    </div>
                </div>
                <div class="p-3">
                    <h3 class="font-bold text-sm text-gray-800 line-clamp-2">{{ product.name }}</h3>
                    <p class="text-xs text-gray-400 mt-1 line-clamp-2">{{ product.description }}</p>
                </div>
            </div>
        </div>
        <div v-else class="flex flex-col items-center justify-center py-20 text-gray-400">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="mb-4 opacity-20"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
            <p>Nenhum produto encontrado</p>
        </div>

        <ProductModal 
            :isOpen="isProductModalOpen" 
            :toggleModal="toggleProductModal"
            :product="selectedProduct"
        />
    </div>
</template>

<script lang="ts" setup>
import { onMounted, ref, computed, watch } from 'vue';
import { categories, products, selectedCommand, organizationSettings, apiBaseUrl } from '@/global/state/GlobalState';
import ProductModal from '@/components/modal/ProductModal.vue';
import axiosClient from '@/api/axios';
import router from '@/router';

const props = defineProps<{
    tab_id?: string
}>()

const selectedProduct = ref<any>(null)
const filteredProducts = ref<any[]>([])
const searchQuery = ref('')
const selectedCategoryId = ref<number | null>(null)
const isProductModalOpen = ref<boolean>(false)

const isSelectedTableOpen = computed(() => {
    return !!selectedCommand.value
})

onMounted(async () => {
    if(!isSelectedTableOpen.value && !props.tab_id){
        router.push({name:'tables'})
        return
    }

    if(props.tab_id && !selectedCommand.value){
        try {
            const res = await axiosClient.get(`/tabs/${props.tab_id}`)
            selectedCommand.value = res.data.data || res.data
        } catch (error) {
            router.push({name:'tables'})
            return
        }
    }

    filterProducts()
})

watch(() => products.value, () => {
    filterProducts()
}, {deep:true})

function filterProducts(){
    filteredProducts.value = products.value.filter((product: any) => {
        const matchesSearch = product.name.toLowerCase().includes(searchQuery.value.toLowerCase())
        const matchesCategory = selectedCategoryId.value ? product.categoryId === selectedCategoryId.value : true
        return matchesSearch && matchesCategory
    })
}

function selectCategory(categoryId: number | null){
    selectedCategoryId.value = categoryId
    filterProducts()
}

watch(searchQuery, () => {
    filterProducts()
})

const currentCategoryName = computed(() => {
    if(!selectedCategoryId.value) return 'Todos os produtos'
    return categories.value.find((c: any) => c.id === selectedCategoryId.value)?.name || ''
})

const setModalProduct = (product: any) => {
    selectedProduct.value = product
    isProductModalOpen.value = true
}

const toggleProductModal = (isOpen: boolean) => {
    isProductModalOpen.value = isOpen
}
</script>

<style scoped>
.box-shadow {
    box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
}
.line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
.scrollbar-hide::-webkit-scrollbar {
    display: none;
}
.scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
}
</style>